import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Image,
  Input,
  Spinner,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
  Wrap,
  WrapItem,
  List,
  ListItem,
  InputRightElement,
  useOutsideClick,
  InputGroup,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { GrUpdate } from "react-icons/gr";
import { FiRefreshCw } from "react-icons/fi";
import { getMediaURL } from "../../context/API";
import CustomizationList from "./CustomizationList";
import { fetchAllCustomizationsAPI, fetchAllCategoriesAPI } from "../../APIs/menuAPI";
import ImagePreview from "../../commonComponents/ImagePreview";
import API from "../../context/API"; 
import { clearCache } from '../../APIs/menuAPI';



const SideBox = ({
  isOpen,
  onClose,
  item,
  onSave,
  imageFile,
  imageInputRef,
  handleImageChange,
  handleDeleteImage,
  onCustomizationClick,
  handleDuplicate,
  customizations,
  setCustomizations,
  categories,
  setCategories,
  loading,
  onCustomizationCreated, // Add this prop
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const suggestionBg = useColorModeValue("white", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const mobileHeaderBg = useColorModeValue("white", "gray.800");
  const mobileHeaderBorder = useColorModeValue("gray.200", "gray.600");
  
  // Define comparison functions BEFORE they're used
  const areItemDataEqual = (obj1, obj2) => {
    // Check if either object is null/undefined
    if (!obj1 || !obj2) return false;
    
    const keys = ["item", "name", "description", "price", "taxable", "sold_out"];
    for (let key of keys) {
      if (obj1[key] !== obj2[key]) return false;
    }
    return true;
  };

  const areArraysEqualById = (arr1, arr2) => {
    // Check if either array is null/undefined
    if (!arr1 || !arr2) return false;
    
    const ids1 = arr1.map((item) => item.id).sort();
    const ids2 = arr2.map((item) => item.id).sort();
    if (ids1.length !== ids2.length) return false;
    for (let i = 0; i < ids1.length; i++) {
      if (ids1[i] !== ids2[i]) return false;
    }
    return true;
  };

  const [currentItem, setCurrentItem] = useState({
    item: "",
    name: "",
    description: "",
    sequence_number: "",
    price: "",
    picture: "",
    taxable: true,
    sold_out: false,
  });
  
  // ... rest of existing state variables ...
  
  const [searchCustomizations, setSearchCustomizations] = useState("");
  const [searchCategories, setSearchCategories] = useState("");
  const [showCustomizationSuggestions, setShowCustomizationSuggestions] = useState(false);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [allCustomizations, setAllCustomizations] = useState([]);
  const [filteredCustomizations, setFilteredCustomizations] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchCustomizationsRef = useRef(null);
  const searchCategoriesRef = useRef(null);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  // Add initial data tracking
  const [initialItemData, setInitialItemData] = useState(null);
  const [initialCustomizations, setInitialCustomizations] = useState([]);
  const [initialCategories, setInitialCategories] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  useOutsideClick({
    ref: searchCustomizationsRef,
    handler: () => setShowCustomizationSuggestions(false),
  });

  useOutsideClick({
    ref: searchCategoriesRef,
    handler: () => setShowCategorySuggestions(false),
  });

  // Main useEffect for setting item data and initial values
  useEffect(() => {
    if (item) {
      // Clear customizations and categories immediately when switching items
      console.log("🔄 Switching to item:", item.id, item.item);
      setCustomizations([]);
      setCategories([]);
      
      const newItemData = {
        item: item.item || "",
        name: item.name || "",
        description: item.description || "",
        sequence_number: item.sequence_number || "",
        price: item.price || "",
        picture: item.picture || "",
        taxable: item.taxable !== undefined ? item.taxable : true,
        sold_out: item.sold_out || false,
      };
      setCurrentItem(newItemData);
      setInitialItemData(newItemData); // Track initial data
      
      // Fetch item's customizations and categories from API
      const fetchItemData = async () => {
        try {
          console.log("🔄 Fetching data for item:", item.id, item.item);
          
          // Fetch customizations
          const customizationsResponse = await API.get(`/items/${item.id}/customizations`);
          console.log("🔄 Customizations API response:", customizationsResponse.data);
          
          if (customizationsResponse.data.success) {
            const rawCustomizations = customizationsResponse.data.data.customizations;
              console.log("🔄 Raw customizations data:", rawCustomizations);
              
              if (rawCustomizations && rawCustomizations.length > 0) {
                console.log("🔄 Total raw customizations:", rawCustomizations.length);
                
                // Log each customization to see the structure
                rawCustomizations.forEach((item, index) => {
                  console.log(`🔄 Customization ${index}:`, {
                    hasItem: !!item,
                    hasCustomization: !!item?.customization,
                    customizationId: item?.customization?.id,
                    customizationName: item?.customization?.customization,
                    sequenceNumber: item?.sequence_number
                  });
                });
                
                const validCustomizations = rawCustomizations.filter(item => item && item.customization);
                console.log("🔄 Valid customizations after filtering:", validCustomizations.length);
                
                // Try to recover null customizations by checking if they have data at the top level
                const recoveredCustomizations = rawCustomizations.filter(item => {
                  if (!item || !item.customization) {
                    // Check if the item itself has customization data at the top level
                    if (item && (item.id || item.customization || item.sequence_number !== undefined)) {
                      console.log("🔄 Found recoverable customization:", item);
                      return true;
                    }
                    return false;
                  }
                  return true;
                });
                
                console.log("🔄 Recovered customizations count:", recoveredCustomizations.length);
                
                if (recoveredCustomizations.length !== rawCustomizations.length) {
                  console.log("🔄 Still filtering out", rawCustomizations.length - recoveredCustomizations.length, "customizations");
                  
                  // Show which ones were filtered out
                  const nullCustomizations = rawCustomizations.filter(item => {
                    if (!item || !item.customization) {
                      return !(item && (item.id || item.customization || item.sequence_number !== undefined));
                    }
                    return false;
                  });
                  console.log("🔄 Null customizations details:", nullCustomizations.map((item, index) => ({
                    index,
                    item: item,
                    hasCustomization: !!item?.customization
                  })));
                }
                
                // Use recovered customizations instead of just valid ones
                const customizationsToProcess = recoveredCustomizations;
              
              const customizationsData = customizationsToProcess.map((item) => {
                // Handle both normal customizations and recovered ones
                let customizationData;
                let options;
                
                if (item.customization) {
                  // Normal case: customization data is in item.customization
                  customizationData = item.customization;
                  options = item.customization.options;
                } else {
                  // Recovered case: customization data might be at the top level
                  customizationData = item;
                  options = item.options;
                }
                
                // Sort options within each customization by sequence_number
                const sortedOptions = options ? 
                  options.sort((a, b) => parseInt(a.sequence_number || 0) - parseInt(b.sequence_number || 0)) :
                  options;
                
                return {
                  ...customizationData,
                  options: sortedOptions,
                  sequence_number: item.sequence_number,
                };
              });
              const sortedCustomizations = customizationsData.sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));
              console.log("🔄 Processed customizations for item", item.id, ":", sortedCustomizations.map(c => ({ 
                id: c.id, 
                customization: c.customization, 
                optionsCount: c.options?.length || 0 
              })));
              setCustomizations(sortedCustomizations);
              setInitialCustomizations(sortedCustomizations);
            } else {
              console.log("🔄 No customizations found for item", item.id);
              setCustomizations([]);
              setInitialCustomizations([]);
            }
          } else {
            console.log("🔄 Customizations API returned success: false", customizationsResponse.data);
            
            // Try alternative approach - check if customizations are in a different structure
            if (customizationsResponse.data.data && customizationsResponse.data.data.length > 0) {
              console.log("🔄 Trying alternative data structure:", customizationsResponse.data.data);
              const alternativeCustomizations = customizationsResponse.data.data.map((customization, index) => ({
                ...customization,
                sequence_number: customization.sequence_number || index + 1,
              }));
              setCustomizations(alternativeCustomizations);
              setInitialCustomizations(alternativeCustomizations);
            } else {
              setCustomizations([]);
              setInitialCustomizations([]);
            }
          }
          
          // Fetch categories
          const categoriesResponse = await API.get(`/items/${item.id}/categories`);
          console.log("🔄 Categories API response:", categoriesResponse.data);
          
          if (categoriesResponse.data.success) {
            const categoriesData = categoriesResponse.data.data.category_names;
            console.log("🔄 Raw categories data:", categoriesData);
            
            if (categoriesData && categoriesData.length > 0) {
              console.log("🔄 Loaded categories for item", item.id, ":", categoriesData.map(c => ({ 
                id: c.id, 
                category: c.category 
              })));
              setCategories(categoriesData);
              setInitialCategories(categoriesData);
            } else {
              console.log("🔄 No categories found for item", item.id);
              setCategories([]);
              setInitialCategories([]);
            }
          } else {
            console.log("🔄 Categories API returned success: false", categoriesResponse.data);
            setCategories([]);
            setInitialCategories([]);
          }
        } catch (error) {
          console.error("🔄 Error fetching item data for item", item.id, ":", error);
          console.error("🔄 Error details:", error.response?.data || error.message);
          
          // Set empty arrays on error to ensure UI shows no data
          setCustomizations([]);
          setInitialCustomizations([]);
          setCategories([]);
          setInitialCategories([]);
        }
      };
      
      fetchItemData();
      
      // Set isChanged to false initially for existing items
      setIsChanged(false);
    } else {
      setCurrentItem({
        item: "",
        name: "",
        description: "",
        sequence_number: "",
        price: "",
        picture: "",
        taxable: true,
        sold_out: false,
      });
      setCustomizations([]);
      setCategories([]);
      setInitialItemData(null);
      setInitialCustomizations([]);
      setInitialCategories([]);
      setIsChanged(true); // Allow creation
    }
  }, [item, setCategories, setCustomizations]);

  // Optimized data loading - API now handles caching
  useEffect(() => {
    const fetchData = async () => {
      // Prevent multiple simultaneous fetches
      if (isLoadingDataRef.current) {
        return;
      }
      
      try {
        isLoadingDataRef.current = true;
        
        // Both API functions now handle caching internally
        const [customizationsData, categoriesData] = await Promise.all([
          fetchAllCustomizationsAPI(),
          fetchAllCategoriesAPI()
        ]);
        
        const validCustomizations = Array.isArray(customizationsData) ? customizationsData : [];
        const validCategories = Array.isArray(categoriesData) ? categoriesData : [];
        
        setAllCustomizations(validCustomizations);
        setFilteredCustomizations([]);
        setAllCategories(validCategories);
        setFilteredCategories([]);
        
        
      } catch (error) {
        console.error('❌ Error fetching data:', error);
        setAllCategories([]);
        setFilteredCategories([]);
        setAllCustomizations([]);
        setFilteredCustomizations([]);
      } finally {
        isLoadingDataRef.current = false;
      }
    };
    
    // Only fetch if SideBox is open AND we don't have data yet
    if (isOpen && allCustomizations.length === 0 && !isLoadingDataRef.current) {
      fetchData();
    }
  }, [isOpen, allCustomizations.length]);

  // Separate useEffect for change detection - runs only when we have initial data
  useEffect(() => {
    // Only run change detection if we have initial data and we're editing an item
    if (item && initialItemData && initialCustomizations && initialCategories) {
      const hasItemDataChanged = !areItemDataEqual(currentItem, initialItemData);
      const hasCustomizationsChanged = !areArraysEqualById(customizations, initialCustomizations);
      const hasCategoriesChanged = !areArraysEqualById(categories, initialCategories);
      const hasImageChanged = !!imageFile;
      setIsChanged(hasItemDataChanged || hasCustomizationsChanged || hasCategoriesChanged || hasImageChanged);
    }
  }, [currentItem, customizations, categories, imageFile, item, initialItemData, initialCustomizations, initialCategories]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentItem((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSoldOutToggle = () => {
    setCurrentItem((prev) => ({
      ...prev,
      sold_out: !prev.sold_out,
    }));
  };

  // Use useRef for timeouts to avoid re-renders
  const searchTimeoutRef = useRef(null);
  const categorySearchTimeoutRef = useRef(null);
  const isLoadingDataRef = useRef(false);

  const handleSearchCustomizations = useCallback((value) => {
    // Update input value immediately for responsive UI
    setSearchCustomizations(value);
    
    // Show loading state if there's a search query
    if (value && value.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setFilteredCustomizations([]);
      setShowCustomizationSuggestions(false);
      return;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce the actual search to improve performance
    searchTimeoutRef.current = setTimeout(() => {
      if (value && value.trim()) {
        const searchQuery = value.toLowerCase().trim();
        
        // Ultra-fast search with early exit after finding 50 matches
        const results = [];
        const maxResults = 50; // Limit results for better performance
        
        for (let i = 0; i < allCustomizations.length && results.length < maxResults; i++) {
          const customization = allCustomizations[i];
          if (customization?.customization?.toLowerCase()?.includes(searchQuery)) {
            results.push(customization);
          }
        }
        
        setFilteredCustomizations(results);
        setShowCustomizationSuggestions(results.length > 0);
        setIsSearching(false);
      } else {
        setFilteredCustomizations([]);
        setShowCustomizationSuggestions(false);
        setIsSearching(false);
      }
    }, 150); // 150ms debounce for better responsiveness
  }, [allCustomizations]);

  const handleSearchCategories = useCallback((value) => {
    // Update input value immediately for responsive UI
    setSearchCategories(value);
    
    // Clear previous timeout
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }
    
    // Debounce the actual search to improve performance
    categorySearchTimeoutRef.current = setTimeout(() => {
      if (value && value.trim()) {
        const searchQuery = value.toLowerCase().trim();
        
        // Ultra-fast search with early exit after finding 50 matches
        const results = [];
        const maxResults = 50; // Limit results for better performance
        
        for (let i = 0; i < allCategories.length && results.length < maxResults; i++) {
          const category = allCategories[i];
          if (category?.category?.toLowerCase()?.includes(searchQuery)) {
            results.push(category);
          }
        }
        
        setFilteredCategories(results);
        setShowCategorySuggestions(results.length > 0);
      } else {
        setFilteredCategories([]);
        setShowCategorySuggestions(false);
      }
    }, 150); // 150ms debounce for better responsiveness
  }, [allCategories]);

  // Memoize filtered results for better performance
  const memoizedCustomizationResults = useMemo(() => {
    return filteredCustomizations.slice(0, 10);
  }, [filteredCustomizations]);

  const memoizedCategoryResults = useMemo(() => {
    return filteredCategories.slice(0, 10);
  }, [filteredCategories]);

  // Re-run search when data becomes available
  useEffect(() => {
    if (searchCustomizations && allCustomizations.length > 0) {
      handleSearchCustomizations(searchCustomizations);
    }
  }, [allCustomizations, searchCustomizations, handleSearchCustomizations]);

  useEffect(() => {
    if (searchCategories && allCategories.length > 0) {
      handleSearchCategories(searchCategories);
    }
  }, [allCategories, searchCategories, handleSearchCategories]);

  const updateCustomizationSequenceNumbers = (customizations) => {
    return customizations.map((customization, index) => ({
      ...customization,
      sequence_number: index,
    }));
  };

  const handleOptionSelectCustomization = (selectedItem) => {
    // Add validation
    if (!selectedItem || !selectedItem.id) {
      console.error('Invalid customization selected:', selectedItem);
      return;
    }
    
    console.log('🎯 Selecting customization:', selectedItem.customization, 'Current customizations:', customizations.length);
    
    if (!customizations.some((customization) => customization.id === selectedItem.id)) {
      const newCustomizations = [...customizations, selectedItem];
      const reorderedCustomizations = updateCustomizationSequenceNumbers(newCustomizations);
      setCustomizations(reorderedCustomizations);
      console.log('✅ Customization added. New count:', reorderedCustomizations.length);
    } else {
      console.log('⚠️ Customization already exists');
    }
    setSearchCustomizations("");
    setShowCustomizationSuggestions(false);
  };

  const handleOptionSelectCategory = (selectedItem) => {
    
    if (!categories.some((category) => category.id === selectedItem.id)) {
      const newCategories = [...categories, selectedItem];
      setCategories(newCategories);
    } else {
    }
    setSearchCategories("");
    setShowCategorySuggestions(false);
  };

  const handleRemoveCustomization = (id) => {
    const updatedCustomizations = customizations.filter((customization) => customization.id !== id);
    const reorderedCustomizations = updateCustomizationSequenceNumbers(updatedCustomizations);
    setCustomizations(reorderedCustomizations);
  };

  const handleRemoveCategory = (id) => {
    const categoriesArray = Array.isArray(categories) ? categories : [];
    setCategories(categoriesArray.filter((category) => category.id !== id));
  };

  const handleSave = () => {
    // Only check for changes if we're editing an existing item
    if (item && initialItemData && initialCustomizations && initialCategories) {
      const hasItemDataChanged = !areItemDataEqual(currentItem, initialItemData);
      const hasCustomizationsChanged = !areArraysEqualById(customizations, initialCustomizations);
      const hasCategoriesChanged = !areArraysEqualById(categories, initialCategories);
      const hasImageChanged = !!imageFile;
      
      if (!hasItemDataChanged && !hasCustomizationsChanged && !hasCategoriesChanged && !hasImageChanged) {
        // Show error or disable button - you can add a toast notification here
        return;
      }
    }
    
    const categoriesArray = Array.isArray(categories) ? categories : [];
    const affectedCategories = categoriesArray.map((category) => category.id);
    onSave(
      {
        ...currentItem,
        customizations: customizations,
        category_names: categoriesArray, // Ensure we always pass an array
      },
      affectedCategories
    );
  };

  const handleSelectAll = () => {
    // Simply use all customizations directly
    const reorderedCustomizations = updateCustomizationSequenceNumbers(allCustomizations);
    setCustomizations(reorderedCustomizations);
  };

  const handleUnselectAll = () => {
    setCustomizations([]);
  };

  const handleClearSearchCustomizations = () => {
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    setSearchCustomizations("");
    setFilteredCustomizations([]);
    setShowCustomizationSuggestions(false);
    setIsSearching(false);
  };

  const handleRefreshCustomizations = async () => {
    try {
      clearCache('allCustomizations');
      const customizationsData = await fetchAllCustomizationsAPI();
      const validCustomizations = Array.isArray(customizationsData) ? customizationsData : [];
      
      setAllCustomizations(validCustomizations);
      // Don't show all customizations after refresh - only show when searching
      setFilteredCustomizations([]);
      
    } catch (error) {
      console.error('❌ Error refreshing customizations:', error);
    }
  };

  const handleClearSearchCategories = () => {
    // Clear any pending search timeout
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
      categorySearchTimeoutRef.current = null;
    }
    
    setSearchCategories("");
    setFilteredCategories([]);
    setShowCategorySuggestions(false);
  };

  const handleImagePreview = (url) => {
    setPreviewImageUrl(url);
    setIsImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setIsImagePreviewOpen(false);
    setPreviewImageUrl("");
  };

  const refreshItemCustomizations = async () => {
    if (!item || !item.id) return;
    
    try {
      console.log('🔄 Refreshing customizations for item:', item.id);
      const customizationsResponse = await API.get(`/items/${item.id}/customizations`);
      if (customizationsResponse.data.success) {
        const customizationsData = customizationsResponse.data.data.customizations.map((item) => {
          // Sort options within each customization by sequence_number
          const sortedOptions = item.customization.options ? 
            item.customization.options.sort((a, b) => parseInt(a.sequence_number || 0) - parseInt(b.sequence_number || 0)) :
            item.customization.options;
          
          console.log(`🔄 Sorted options for ${item.customization.customization}:`, sortedOptions?.map(opt => ({ id: opt.id, option: opt.option, sequence_number: opt.sequence_number })));
          
          return {
            ...item.customization,
            options: sortedOptions,
            sequence_number: item.sequence_number,
          };
        });
        const sortedCustomizations = customizationsData.sort((a, b) => parseInt(a.sequence_number) - parseInt(b.sequence_number));
        console.log('🔄 Updated customizations with sorted options:', sortedCustomizations);
        setCustomizations(sortedCustomizations);
        setInitialCustomizations(sortedCustomizations);
      }
    } catch (error) {
      console.error('❌ Error refreshing item customizations:', error);
    }
  };

  const refreshCustomizations = async () => {
    try {
      
      // Clear cache to ensure fresh data
      clearCache('allCustomizations');
      
      const customizationsData = await fetchAllCustomizationsAPI();
      
      const validCustomizations = Array.isArray(customizationsData) ? customizationsData : [];
      
      setAllCustomizations(validCustomizations);
      // Don't show all customizations after refresh - only show when searching
      setFilteredCustomizations([]);
      
      // If we're currently editing an item, also refresh its customizations
      if (item && item.id) {
        await refreshItemCustomizations();
      }
      
      // Don't automatically set all customizations to parent - only set when explicitly selected
      // The parent component's customizations state should remain empty until user selects specific ones
      
    } catch (error) {
      console.error('❌ Error refreshing customizations:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      refreshCustomizations();
    }
    // Only run when sidebar opens; omit refreshCustomizations to avoid infinite loop
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onCustomizationCreated) {
      onCustomizationCreated();
    }
  }, [onCustomizationCreated]);

  useEffect(() => {
    const handleCustomizationCreated = (event) => {
      refreshCustomizations();
    };

    const handleCustomizationUpdated = (event) => {
      console.log('🔄 CustomizationUpdated event received in SideBox:', event.detail);
      refreshCustomizations();
      
      // Also refresh the currently selected customizations for the item
      if (item && item.id && customizations.length > 0) {
        console.log('🔄 Refreshing selected customizations due to update event');
        const updatedCustomizationId = event.detail?.customization?.id;
        
        // If the updated customization is one of the selected ones, refresh the entire list
        if (updatedCustomizationId && customizations.some(c => c.id === updatedCustomizationId)) {
          console.log('🔄 Updated customization is in selected list, refreshing...');
          refreshItemCustomizations();
        }
      }
    };

    const handleOptionsReordered = (event) => {
      console.log('🔄 OptionsReordered event received in SideBox:', event.detail);
      console.log('🔄 Current item:', item);
      console.log('🔄 Current customizations in SideBox:', customizations.map(c => ({ id: c.id, optionCount: c.option?.length })));
      // Refresh the item's customizations to reflect the new order
      if (item && item.id) {
        console.log('🔄 Refreshing item customizations...');
        refreshItemCustomizations();
      }
    };

    window.addEventListener('customizationCreated', handleCustomizationCreated);
    window.addEventListener('customizationUpdated', handleCustomizationUpdated);
    window.addEventListener('optionsReordered', handleOptionsReordered);

    return () => {
      window.removeEventListener('customizationCreated', handleCustomizationCreated);
      window.removeEventListener('customizationUpdated', handleCustomizationUpdated);
      window.removeEventListener('optionsReordered', handleOptionsReordered);
    };
    // Register once on mount; handlers call refresh functions which fetch fresh data
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  return (
    <Box
      position={{ base: "fixed", lg: "relative" }}
      top={{ base: 0, lg: "auto" }}
      right={{ base: 0, lg: "auto" }}
      bottom={{ base: 0, lg: "auto" }}
      width={{ base: "100%", lg: "400px" }}
      height={{ base: "100vh", lg: "auto" }}
      zIndex={{ base: 1000, lg: "auto" }}
      bg={bgColor}
      boxShadow={{ base: "lg", lg: "none" }}
      overflowY="auto"
      borderLeft={{ base: "none", lg: "1px solid" }}
      borderColor={{ base: "transparent", lg: borderColor }}
    >
      {/* Mobile header */}
      {isMobile && (
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottom="1px"
          borderColor={mobileHeaderBorder}
          bg={mobileHeaderBg}
          position="sticky"
          top={0}
          zIndex={1}
        >
          <Text fontWeight="bold" fontSize="lg">
            {item ? "Edit Item" : "Add Item"}
          </Text>
          <IconButton
            icon={<CloseIcon />}
            onClick={onClose}
            variant="ghost"
            size="sm"
            aria-label="Close sidebar"
          />
        </Flex>
      )}

      {/* Content */}
      <Box p={{ base: 4, lg: 6 }}>
        {/* Desktop close button */}
        {!isMobile && (
          <Flex justify="flex-end" mb={4}>
            <IconButton
              icon={<CloseIcon />}
              onClick={onClose}
              variant="ghost"
              size="sm"
              aria-label="Close sidebar"
            />
          </Flex>
        )}

        <VStack spacing={6} align="stretch">
          {/* Item Name */}
          <FormControl>
            <FormLabel fontSize={{ base: "sm", md: "md" }}>Item Name</FormLabel>
            <Input
              name="item"
              value={currentItem.item}
              onChange={handleInputChange}
              placeholder="Enter item name"
              size={{ base: "md", md: "lg" }}
            />
          </FormControl>

          {/* Description */}
          <FormControl>
            <FormLabel fontSize={{ base: "sm", md: "md" }}>Description</FormLabel>
            <Textarea
              name="description"
              value={currentItem.description}
              onChange={handleInputChange}
              placeholder="Enter item description"
              size={{ base: "md", md: "lg" }}
              rows={3}
            />
          </FormControl>

          {/* Price */}
          <FormControl>
            <FormLabel fontSize={{ base: "sm", md: "md" }}>Price</FormLabel>
            <Input
              name="price"
              type="number"
              step="0.01"
              value={currentItem.price}
              onChange={handleInputChange}
              placeholder="0.00"
              size={{ base: "md", md: "lg" }}
            />
          </FormControl>

          {/* Image Upload */}
          <FormControl>
            <FormLabel fontSize={{ base: "sm", md: "md" }}>Image</FormLabel>
            <Box position="relative">
              <Image
                src={
                  imageFile
                    ? URL.createObjectURL(imageFile)
                    : currentItem.picture
                    ? getMediaURL(currentItem.picture)
                    : "https://via.placeholder.com/320x240"
                }
                alt="Item Image"
                width="100%"
                height="200px"
                objectFit="cover"
                borderRadius="md"
                fallbackSrc="https://via.placeholder.com/320x240"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/320x240";
                }}
                onLoad={(e) => {
                }}
                onClick={() => {
                  if (item) {
                    // When editing existing item, show image preview
                    handleImagePreview(
                      imageFile
                        ? URL.createObjectURL(imageFile)
                        : currentItem.picture
                        ? getMediaURL(currentItem.picture)
                        : "https://via.placeholder.com/320x240"
                    );
                  } else {
                    // When adding new item, open image picker
                    imageInputRef.current.click();
                  }
                }}
                cursor="pointer"
                transition="transform 0.2s"
                _hover={{
                  transform: "scale(1.02)",
                  boxShadow: "lg"
                }}
              />
              <HStack position="absolute" top="2" right="2" spacing="2">
                <Button
                  onClick={() => imageInputRef.current.click()}
                  leftIcon={item ? <GrUpdate /> : <AddIcon />}
                  bg="black"
                  color="white"
                  _hover={{
                    color: "black",
                    bg: "white",
                    borderWidth: "1px",
                    borderColor: "gray.300",
                  }}
                  variant="outline"
                  size="sm"
                  borderRadius="full"
                >
                  {item ? "Change" : "Add"}
                </Button>
                {(imageFile || (item && currentItem.picture && currentItem.picture !== "default.jpg" && currentItem.picture.trim() !== "")) && (
                  <IconButton
                    onClick={handleDeleteImage}
                    icon={<DeleteIcon />}
                    bg="red.500"
                    color="white"
                    _hover={{
                      bg: "red.600",
                    }}
                    size="sm"
                    borderRadius="full"
                    aria-label="Delete image"
                  />
                )}
              </HStack>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={imageInputRef}
                style={{ display: "none" }}
              />
            </Box>
          </FormControl>

          {/* Taxable Switch */}
          <FormControl display="flex" alignItems="center">
            <FormLabel fontSize={{ base: "sm", md: "md" }} mb="0">
              Taxable
            </FormLabel>
            <Switch
              name="taxable"
              isChecked={currentItem.taxable}
              onChange={handleInputChange}
              colorScheme="green"
            />
          </FormControl>

          {/* Sold Out Switch */}
          <FormControl display="flex" alignItems="center">
            <FormLabel fontSize={{ base: "sm", md: "md" }} mb="0">
              Sold Out
            </FormLabel>
            <Switch
              name="sold_out"
              isChecked={currentItem.sold_out}
              onChange={handleSoldOutToggle}
              colorScheme="red"
            />
          </FormControl>

          <Divider />

          {/* Customizations Section */}
          <Box>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>
                Customizations
              </Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  onClick={handleSelectAll}
                  variant="outline"
                >
                  Select All
                </Button>
                <Button
                  size="xs"
                  onClick={handleUnselectAll}
                  variant="outline"
                >
                  Clear All
                </Button>
              </HStack>
            </Flex>

            {/* Customization Search */}
            <Box position="relative" mb={3} ref={searchCustomizationsRef}>
              <InputGroup>
                <Input
                  placeholder="Search customizations"
                  value={searchCustomizations}
                  onChange={(e) => handleSearchCustomizations(e.target.value)}
                  size={{ base: "md", md: "lg" }}
                />
                <InputRightElement>
                  {isSearching ? (
                    <Spinner size="sm" />
                  ) : searchCustomizations ? (
                    <IconButton
                      icon={<CloseIcon />}
                      size="sm"
                      onClick={handleClearSearchCustomizations}
                      variant="ghost"
                      aria-label="Clear search"
                    />
                  ) : (
                    <IconButton
                      icon={<FiRefreshCw />}
                      size="sm"
                      onClick={handleRefreshCustomizations}
                      variant="ghost"
                      aria-label="Refresh customizations"
                      title="Refresh customizations list"
                    />
                  )}
                </InputRightElement>
              </InputGroup>

              {/* Customization Suggestions */}
              {showCustomizationSuggestions && filteredCustomizations.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0"
                  right="0"
                  zIndex="10"
                  bg={suggestionBg}
                  borderRadius="md"
                  boxShadow="lg"
                  maxHeight="200px"
                  overflowY="auto"
                  mt={1}
                >
                  <List spacing={0}>
                    {memoizedCustomizationResults.map((customization) => (
                      <ListItem
                        key={customization.id}
                        p={3}
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleOptionSelectCustomization(customization)}
                      >
                        {customization.customization}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>

            {/* Selected Customizations */}
            <CustomizationList
              customizations={customizations}
              setCustomizations={setCustomizations}
              onCustomizationClick={onCustomizationClick}
              handleRemoveCustomization={handleRemoveCustomization}
            />
          </Box>

          <Divider />

          {/* Categories Section */}
          <Box>
            <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }} mb={3}>
              Categories
            </Text>

            {/* Category Search */}
            <Box position="relative" mb={3} ref={searchCategoriesRef}>
              <InputGroup>
                <Input
                  placeholder="Search categories"
                  value={searchCategories}
                  onChange={(e) => handleSearchCategories(e.target.value)}
                  size={{ base: "md", md: "lg" }}
                />
                {searchCategories && (
                  <InputRightElement>
                    <IconButton
                      icon={<CloseIcon />}
                      size="sm"
                      onClick={handleClearSearchCategories}
                      variant="ghost"
                      aria-label="Clear search"
                    />
                  </InputRightElement>
                )}
              </InputGroup>

              {/* Category Suggestions */}
              {showCategorySuggestions && filteredCategories.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0"
                  right="0"
                  zIndex="10"
                  bg={suggestionBg}
                  borderRadius="md"
                  boxShadow="lg"
                  maxHeight="200px"
                  overflowY="auto"
                  mt={1}
                >
                  <List spacing={0}>
                    {memoizedCategoryResults.map((category) => (
                      <ListItem
                        key={category.id}
                        p={3}
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleOptionSelectCategory(category)}
                      >
                        {category.category}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>

            {/* Selected Categories */}
            <Wrap spacing={2}>
              {Array.isArray(categories) ? categories.map((category) => (
                <WrapItem key={category.id}>
                  <Badge
                    colorScheme="blue"
                    variant="solid"
                    p={2}
                    borderRadius="full"
                  >
                    {category.category}
                    <IconButton
                      icon={<CloseIcon />}
                      size="xs"
                      ml={2}
                      onClick={() => handleRemoveCategory(category.id)}
                      variant="unstyled"
                      aria-label="Remove category"
                      _hover={{ color: "red.500" }}
                    />
                  </Badge>
                </WrapItem>
              )) : null}
            </Wrap>
          </Box>

          {/* Action Buttons */}
          <VStack spacing={3} pt={4}>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              size={{ base: "md", md: "lg" }}
              width="100%"
              isLoading={loading}
              isDisabled={!isChanged} // Disable when no changes
            >
              {item ? "Update Item" : "Create Item"}
            </Button>
            
            {item && (
              <Button
                variant="outline"
                onClick={() => {
                  const categoriesArray = Array.isArray(categories) ? categories : [];
                  handleDuplicate(categoriesArray.map(cat => cat.id));
                }}
                size={{ base: "md", md: "lg" }}
                width="100%"
              >
                Duplicate Item
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>

      <ImagePreview
        isOpen={isImagePreviewOpen}
        onClose={handleCloseImagePreview}
        imageUrl={previewImageUrl}
      />
    </Box>
  );
};

export default SideBox;
